<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'next/dialog/Dialog.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  reports: {
    type: Array,
    default: () => [],
  },
  excludeReportId: {
    type: [Number, String, null],
    default: null,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const search = ref('');

const VISUALIZATION_ICONS = {
  bar_chart: 'i-lucide-bar-chart-3',
  line_chart: 'i-lucide-line-chart',
  pie_chart: 'i-lucide-pie-chart',
  table: 'i-lucide-table',
};

const availableReports = computed(() =>
  props.reports.filter(r => Number(r.id) !== Number(props.excludeReportId))
);

const filteredReports = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return availableReports.value;
  return availableReports.value.filter(r => {
    const hay = `${r.name || ''} ${r.description || ''}`.toLowerCase();
    return hay.includes(q);
  });
});

const hasAnyReports = computed(() => props.reports.length > 0);

const open = () => {
  search.value = '';
  isOpen.value = true;
  nextTick(() => dialogRef.value?.open());
};

const close = () => {
  dialogRef.value?.close();
  isOpen.value = false;
};

const onSelect = report => {
  emit('select', report);
  close();
};

const visualizationIcon = report =>
  VISUALIZATION_ICONS[report?.config?.default_visualization] ||
  'i-lucide-bar-chart-3';

const metricCountLabel = report => {
  const count = (report?.config?.metrics || []).length;
  if (count === 1) {
    return t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.METRIC_COUNT_ONE');
  }
  return t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.METRIC_COUNT_MANY', { count });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    position="top"
    :title="t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.TITLE')"
    :description="t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.CANCEL')"
    overflow-y-auto
    @close="isOpen = false"
  >
    <div class="flex flex-col gap-4">
      <div v-if="hasAnyReports" class="relative flex items-center w-full">
        <Icon
          icon="i-lucide-search"
          class="absolute start-3 text-n-slate-10 size-4"
        />
        <input
          v-model="search"
          type="text"
          autofocus
          :placeholder="
            t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.SEARCH_PLACEHOLDER')
          "
          class="reset-base w-full ps-10 pe-3 py-2.5 text-sm rounded-lg outline-none focus:outline-none bg-n-alpha-1 border border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand"
        />
      </div>

      <div
        v-if="!hasAnyReports"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <Icon icon="i-lucide-bar-chart-3" class="size-10 text-n-slate-9 mb-3" />
        <p class="text-sm text-n-slate-11">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.NO_REPORTS') }}
        </p>
      </div>

      <div
        v-else-if="!filteredReports.length"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <Icon icon="i-lucide-search-x" class="size-10 text-n-slate-9 mb-3" />
        <p class="text-sm text-n-slate-11">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.PICKER.EMPTY_SEARCH') }}
        </p>
      </div>

      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1"
      >
        <button
          v-for="report in filteredReports"
          :key="report.id"
          type="button"
          class="text-left bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak hover:border-n-brand rounded-lg p-3 transition flex flex-col gap-2"
          @click="onSelect(report)"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex items-center justify-center size-9 rounded-md bg-n-brand/10 text-n-brand shrink-0"
            >
              <Icon :icon="visualizationIcon(report)" class="size-5" />
            </div>
            <div class="min-w-0 flex-1">
              <h4 class="text-sm font-medium text-n-slate-12 truncate">
                {{ report.name }}
              </h4>
              <p
                v-if="report.description"
                class="text-xs text-n-slate-11 line-clamp-2 mt-0.5"
              >
                {{ report.description }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="inline-flex items-center gap-1">
              <Icon icon="i-lucide-ruler" class="size-3" />
              {{ metricCountLabel(report) }}
            </span>
          </div>
        </button>
      </div>
    </div>
  </Dialog>
</template>
